import {useState} from 'react';
import styled from 'styled-components';
import {EmptyState} from './EmptyState';
import {ScrollableTable} from './ScrollableTable';
import {graphql, useFragment} from 'react-relay';
import type {TimeTab_ticket$key} from './__generated__/TimeTab_ticket.graphql';
import type {ticketingPerson_person$key} from '../../../graphql/fragments/__generated__/ticketingPerson_person.graphql';
import {ticketingPersonFragment, personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {formatDateFull} from '../utils/formatDate';
import {LogTimeModal, type LogTimeModalTask} from './LogTimeModal';
import {
	isTaskAssignmentAllowedForTimeRegistration,
	isTimeRegistrationTaskAssignmentRequired,
} from '../../../graphql/forecastRelay';

// `ticketTimeRegistrations` is the time logged THROUGH this ticket. Walking
// `linkedTasks { timeRegistrations }` instead would pull in every registration on
// a linked task — including time booked from Forecast's own timesheet or via
// another ticket — which is what inflated the table and the stat cards.
//
// `linkedTasks` is still selected, but only for the Log time modal's task picker.
// `assignedPersons` feeds the "Require task assignment" filter on that picker.
const TimeTabFragment = graphql`
	fragment TimeTab_ticket on Ticket {
		id
		linkedTasks {
			id
			name
			project {
				id
			}
			assignedPersons {
				id
			}
		}
		ticketTimeRegistrations {
			id
			minutesRegistered
			billableMinutesRegistered
			notes
			date
			day
			month
			year
			task {
				name
			}
			person {
				...ticketingPerson_person
			}
			role {
				name
			}
		}
	}
`;

interface Props {
	ticket: TimeTab_ticket$key;
	currentPersonId: string;
	onRefetch?: () => void;
}

interface Row {
	id: string;
	taskName: string;
	person: ticketingPerson_person$key | null;
	roleName: string | null;
	date: string | null;
	sortKey: number;
	note: string | null;
	minutes: number;
	billableMinutes: number;
}

const Stack = styled.div`
	> * + * {
		margin-top: 24px;
	}
`;
const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const Title = styled.h3`
	margin: 0;
	${p => p.theme.sys.font?.subheading?.['1']}
	letter-spacing: 0.15px;
	color: #0f172a;
`;
const PrimaryButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border: 0;
	border-radius: 6px;
	background: #4f47d6;
	padding: 6px 12px;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #ffffff;
	cursor: pointer;
	&:hover {
		background: #3d36b3;
	}
`;
const StatGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 16px;
`;
const StatCard = styled.div`
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 16px;
`;
const StatLabel = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
`;
const StatValue = styled.p`
	margin: 4px 0 0;
	font-size: 24px;
	line-height: 32px;
	font-weight: 600;
	color: #0f172a;
`;
const EmptyCard = styled(EmptyState)`
	border-style: dashed;
	background: ${p => p.theme.sys.color.background.accent['1']};
`;
const EmptyText = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #475569;
`;
const TableCard = styled.div`
	overflow: hidden;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;
// `table-layout: fixed` is what makes the per-column widths below binding rather than
// advisory, so a long note or task name truncates instead of stretching the column and
// pushing Hours off the edge. The `min-width` is the five fixed columns (770px) plus a
// readable 200px for Note: below it the table stops shrinking and the surrounding
// ScrollableTable takes over, matching how the design-system tables behave.
const Table = styled.table`
	width: 100%;
	min-width: 970px;
	table-layout: fixed;
	border-collapse: collapse;
`;
const Thead = styled.thead`
	background: #f8fafc;
`;
const Th = styled.th<{$right?: boolean; $width?: string}>`
	padding: 10px 16px;
	text-align: ${p => (p.$right ? 'right' : 'left')};
	${p => (p.$width ? `width: ${p.$width};` : '')}
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
	border-bottom: 1px solid #e2e8f0;
`;
const Tr = styled.tr`
	border-top: 1px solid #f1f5f9;
	&:hover {
		background: #f8fafc;
	}
`;
const Td = styled.td<{$nowrap?: boolean; $right?: boolean; $truncate?: boolean}>`
	padding: 10px 16px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #475569;
	${p => p.$nowrap && 'white-space: nowrap;'}
	${p => p.$right && 'text-align: right;'}
	${p => p.$truncate && 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'}
`;
const StrongTd = styled(Td)`
	color: #0f172a;
`;
const HoursTd = styled(Td)`
	font-weight: 500;
	color: #0f172a;
`;
const BillableNote = styled.span`
	margin-left: 4px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #64748b;
`;

/** Format minutes as a one-decimal hour value, e.g. 90 → "1.5h". */
function hoursLabel(minutes: number): string {
	return `${(minutes / 60).toFixed(1)}h`;
}

function PersonCell({person}: {person: ticketingPerson_person$key | null}) {
	const data = useFragment(ticketingPersonFragment, person);
	const name = personDisplayName(data);
	// The name is the cell's whole content, so the truncation tooltip has to be carried
	// here — the cell cannot title an element it does not render.
	return <span title={name}>{name}</span>;
}

export function TimeTab({ticket: ticketRef, currentPersonId, onRefetch}: Props) {
	const data = useFragment(TimeTabFragment, ticketRef);
	const [logOpen, setLogOpen] = useState(false);

	// With "Require task assignment for time entries" on, only the viewer's own tasks are
	// offered — the same rule the timesheet's task pickers apply. Off, every linked task is.
	const tasks: LogTimeModalTask[] = data.linkedTasks
		.filter(t =>
			isTaskAssignmentAllowedForTimeRegistration(
				currentPersonId,
				(t.assignedPersons ?? []).flatMap(p => (p ? [p.id] : []))
			)
		)
		.map(t => ({
			id: t.id,
			name: t.name ?? 'Untitled task',
			projectId: t.project?.id ?? null,
		}));

	// Each registration carries its own task, so the Task column no longer needs
	// the linkedTasks walk.
	const rows: Row[] = data.ticketTimeRegistrations.map(reg => ({
		id: reg.id,
		taskName: reg.task?.name ?? 'Untitled task',
		person: reg.person ?? null,
		roleName: reg.role?.name ?? null,
		date: reg.date ?? null,
		sortKey: (reg.year ?? 0) * 10000 + (reg.month ?? 0) * 100 + (reg.day ?? 0),
		note: reg.notes ?? null,
		minutes: reg.minutesRegistered ?? 0,
		billableMinutes: reg.billableMinutesRegistered ?? 0,
	}));
	rows.sort((a, b) => b.sortKey - a.sortKey);

	const totalMinutes = rows.reduce((sum, r) => sum + r.minutes, 0);
	const totalBillable = rows.reduce((sum, r) => sum + r.billableMinutes, 0);

	return (
		<Stack>
			<HeaderRow>
				<Title>Time</Title>
				{/* Keyed on the linked tasks, not the filtered picker list: a user assigned to none
				    of them still gets the modal, which explains why there is nothing to select. */}
				{data.linkedTasks.length > 0 && (
					<PrimaryButton type="button" onClick={() => setLogOpen(true)}>
						+ Log time
					</PrimaryButton>
				)}
			</HeaderRow>

			<StatGrid>
				<StatCard>
					<StatLabel>Total logged</StatLabel>
					<StatValue>{hoursLabel(totalMinutes)}</StatValue>
				</StatCard>
				<StatCard>
					<StatLabel>Billable</StatLabel>
					<StatValue>{hoursLabel(totalBillable)}</StatValue>
				</StatCard>
				<StatCard>
					<StatLabel>Entries</StatLabel>
					<StatValue>{rows.length}</StatValue>
				</StatCard>
			</StatGrid>

			{rows.length === 0 ? (
				<EmptyCard>
					<EmptyText>No time logged yet.</EmptyText>
				</EmptyCard>
			) : (
				<TableCard>
					<ScrollableTable data-testid="time-table-scroll">
						<Table>
							<Thead>
								<tr>
									<Th $width="220px">Task</Th>
									<Th $width="160px">Person</Th>
									<Th $width="140px">Role</Th>
									{/* Wide enough for the "12 Mar 2026" reading — squeezing this column,
									    which is what happened before, cut the year off mid-digit. */}
									<Th $width="130px">Date</Th>
									{/* Note is the one flexible column: it absorbs whatever width is left
									    over, and truncates when there is none to absorb. */}
									<Th>Note</Th>
									<Th $right $width="120px">
										Hours
									</Th>
								</tr>
							</Thead>
							<tbody>
								{rows.map(row => (
									<Tr key={row.id}>
										<StrongTd $truncate title={row.taskName}>
											{row.taskName}
										</StrongTd>
										<Td $truncate>
											<PersonCell person={row.person} />
										</Td>
										<Td $truncate title={row.roleName ?? undefined}>
											{row.roleName ?? '—'}
										</Td>
										<Td $nowrap>{row.date ? formatDateFull(row.date) : '—'}</Td>
										<Td $truncate title={row.note ?? undefined}>
											{row.note ?? '—'}
										</Td>
										<HoursTd $nowrap $right>
											{(row.minutes / 60).toFixed(1)}
											{row.billableMinutes !== row.minutes && (
												<BillableNote>({(row.billableMinutes / 60).toFixed(1)} billable)</BillableNote>
											)}
										</HoursTd>
									</Tr>
								))}
							</tbody>
						</Table>
					</ScrollableTable>
				</TableCard>
			)}

			{logOpen && (
				<LogTimeModal
					open
					personId={currentPersonId}
					ticketId={data.id}
					tasks={tasks}
					noTasksMessage={
						isTimeRegistrationTaskAssignmentRequired()
							? "You're not assigned to any of this ticket's tasks. Your company only allows logging time on tasks you're assigned to — ask a project owner to assign you."
							: undefined
					}
					onClose={() => setLogOpen(false)}
					onSuccess={() => {
						setLogOpen(false);
						onRefetch?.();
					}}
				/>
			)}
		</Stack>
	);
}
